import { html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { ref } from 'lit/directives/ref.js'
import { AriaDescriptionController } from '../common/controllers/AriaDescriptionController.js'
import { observe } from '../common/decorators/observe.js'

import { NordEvent } from '../common/events.js'
import { AutocompleteMixin } from '../common/mixins/AutocompleteMixin.js'
import { FocusableMixin } from '../common/mixins/FocusableMixin.js'
import { FormAssociatedMixin } from '../common/mixins/FormAssociatedMixin.js'
import { InputMixin } from '../common/mixins/InputMixin.js'
import { ReadonlyMixin } from '../common/mixins/ReadonlyMixin.js'
import { SizeMixin } from '../common/mixins/SizeMixin.js'

import { TextSelectableMixin } from '../common/mixins/TextSelectableMixin.js'
import componentStyle from '../common/styles/Component.css'
import formFieldStyle from '../common/styles/FormField.css'
import textFieldStyle from '../common/styles/TextField.css'
import { requestInputGroupContext } from '../input-group/_InputGroupContext.js'
import { InputGroupControlController } from '../input-group/controllers/InputGroupControlController.js'
import inputGroupControlStyle from '../input-group/InputGroupControl.css'
import { LocalizeController } from '../localization/LocalizeController.js'
import style from './Textarea.css'

function createLengthMeasurer(locale: string) {
  if (Intl.Segmenter) {
    const segmenter = new Intl.Segmenter(locale)
    return (value: string) => [...segmenter.segment(value)].length
  }

  return (value: string) => value.length
}

/**
 * Textarea is a component that allows user to write text over
 * multiple rows. Used when the expected user input is long.
 * For shorter input, use the Input component.
 *
 * @status ready
 * @category form
 * @slot label - Use when a label requires more than plain text.
 * @slot hint - Optional slot that holds hint text for the textarea.
 * @slot error - Optional slot that holds error text for the textarea.
 *
 * @cssprop [--n-textarea-inline-size=240px] - Controls the inline size, or width, of the textarea.
 * @cssprop [--n-textarea-block-size=auto] - Raises the minimum block size, or height, of the textarea above the height derived from `rows`. The effective minimum is the larger of this and the `rows` height. When `resize="auto"`, the textarea grows from that minimum as content is added.
 * @cssprop [--n-textarea-max-block-size=500px] - Controls the maximum block size, or height, of the textarea. When `resize="auto"`, the textarea stops growing at this height and a scrollbar appears.
 * @cssprop [--n-textarea-background=var(--n-color-active)] - Controls the background of the textarea, using our [color tokens](/tokens/#color).
 * @cssprop [--n-textarea-color=var(--n-color-text)] - Controls the text color of the textarea, using our [color tokens](/tokens/#color).
 * @cssprop [--n-textarea-border-color=var(--n-color-border-strong)] - Controls the border color of the textarea, using our [color tokens](/tokens/#color).
 * @cssprop [--n-textarea-border-radius=var(--n-border-radius-s)] - Controls how rounded the corners are, using [border radius tokens](/tokens/#border-radius).
 * @cssprop [--n-label-color=var(--n-color-text)] - Controls the text color of the label, using our [color tokens](/tokens/#color).
 *
 * @localization remainingCharacters - A function which receives the number of remaining characters and returns a string to be used as the aria-live message.
 */
@customElement('nord-textarea')
export default class Textarea extends SizeMixin(
  FormAssociatedMixin(AutocompleteMixin(ReadonlyMixin(TextSelectableMixin(InputMixin(FocusableMixin(LitElement)))))),
) {
  static styles = [componentStyle, formFieldStyle, textFieldStyle, style, inputGroupControlStyle]

  protected inputId = 'textarea'

  private lengthMeasurer!: (value: string) => number
  private localize = new LocalizeController<'nord-textarea'>(this, {
    onLangChange: () => this.handleLangChange(),
  })

  private inputGroupControl = new InputGroupControlController(this, () => this.focusableRef.value)
  private ariaDescriptionController = new AriaDescriptionController(
    this,
    () => this.focusableRef.value,
    () => this.controlAriaDescribedByElements,
  )

  /**
   * Controls whether the textarea is resizable.
   * By default is manually resizable vertically.
   * Set to "auto" to enable auto-resizing as content grows.
   */
  @property({ reflect: true }) resize: 'vertical' | 'auto' = 'vertical'

  /**
   * Controls whether the textarea expands to fill the width of its container.
   */
  @property({ reflect: true, type: Boolean }) expand = false

  /**
   * Controls the number of visible text rows, setting the textarea's minimum height. Mirrors the
   * native [`rows`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/textarea#rows)
   * attribute. When `resize="auto"`, this is the starting height the textarea grows from — e.g.
   * `rows="1"` for a single-row composer that expands as the user types. The
   * `--n-textarea-block-size` Custom Property can raise the minimum height above the row-derived
   * one (the effective minimum is the larger of the two).
   */
  @property({ reflect: true, type: Number }) rows = 3

  /**
   * Controls the max allowed length for the textarea.
   */
  @property({ reflect: true, attribute: 'maxlength', type: Number }) maxLength?: number

  /**
   * Controls whether to show a count of the number of characters in the textarea.
   * When combined with `maxlength`, both the count and the max length are shown.
   */
  @property({ reflect: true, type: Boolean, attribute: 'character-counter' }) characterCounter = false

  render() {
    return html`
      ${this.renderLabel()}

      <div
        class=${classMap({
          'n-input-container': true,
          'is-input-group-control': this.inputGroupControl.grouped,
          'is-input-group-size-s': this.inputGroupControl.size === 's',
          'is-input-group-size-m': this.inputGroupControl.size === 'm',
          'is-input-group-size-l': this.inputGroupControl.size === 'l',
          'has-input-group-inline-start-addon': this.inputGroupControl.hasInlineStartAddon,
          'has-input-group-inline-end-addon': this.inputGroupControl.hasInlineEndAddon,
        })}
      >
        <textarea
          ${ref(this.textSelectableRef)}
          ${ref(this.focusableRef)}
          id=${this.inputId}
          class="n-input"
          ?disabled=${this.effectiveDisabled}
          ?readonly=${this.readonly || this.inputGroupControl.readonly}
          ?required=${this.required || this.inputGroupControl.required}
          name=${ifDefined(this.name)}
          maxlength=${ifDefined(this.maxLength)}
          rows=${ifDefined(this.rows)}
          .value=${this.value ?? ''}
          placeholder=${ifDefined(this.placeholder)}
          @change=${this.handleChange}
          @input=${this.handleInput}
          @select=${this.handleSelect}
          aria-label=${ifDefined(this.controlAriaLabel)}
          aria-describedby=${ifDefined(this.controlAriaDescribedby)}
          aria-invalid=${ifDefined(this.controlAriaInvalid)}
          autocomplete=${this.autocomplete as any}
        ></textarea>

        ${this.characterCounter ? this.renderCharacterCounter() : nothing}
      </div>

      ${this.renderError()}
      ${this.isHintBelow ? this.renderHint() : nothing}
    `
  }

  [requestInputGroupContext]() {
    this.requestFieldContext()
    this.inputGroupControl.requestContext()
  }

  protected handleSelect(e: Event) {
    e.stopPropagation()

    /**
     * Fired when some text has been selected.
     */
    this.dispatchEvent(new NordEvent('select'))
  }

  private renderCharacterCounter() {
    const { value, maxLength } = this
    const length = typeof value === 'string' ? this.lengthMeasurer(value) : 0

    const remainder = maxLength ? maxLength - length : null
    const counter = maxLength ? `${length}/${maxLength}` : length

    return html`
      <nord-visually-hidden aria-live="polite" aria-atomic="true">
        ${remainder != null && remainder <= 10 ? this.localize.term('remainingCharacters', remainder) : ''}
      </nord-visually-hidden>
      <div class="n-character-counter">${counter}</div>
    `
  }

  private handleLangChange() {
    const lang = this.localize.resolvedLang
    this.lengthMeasurer = createLengthMeasurer(lang)
  }

  @observe('resize', 'updated')
  @observe('value', 'updated')
  protected resizeToFitContent() {
    const textarea = this.focusableRef.value

    if (!textarea) {
      return
    }

    if (this.resize === 'auto') {
      textarea.style.height = 'auto'
      // scrollHeight includes padding but not border. With box-sizing: border-box,
      // setting height directly to scrollHeight would shrink the content area by the
      // border width and clip the last line. Add the border so padding-block is preserved,
      // then clamp the result to the resolved min/max so the inline height never
      // overshoots the cap.
      const computed = getComputedStyle(textarea)
      const borderSize = textarea.offsetHeight - textarea.clientHeight
      const minHeight = Number.parseFloat(computed.minBlockSize) || 0
      const maxHeight = Number.parseFloat(computed.maxBlockSize) || Number.POSITIVE_INFINITY
      const desired = textarea.scrollHeight + borderSize

      textarea.style.height = `${Math.min(Math.max(desired, minHeight), maxHeight)}px`
    }
    else {
      textarea.style.height = ''
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-textarea': Textarea
  }
}
